import React from 'react';
import RequiredReasonDialog from './RequiredReasonDialog';
import { OVERRIDE_REASON_MAX } from '../constants/overrideReason';
import {
  APPROVE_ON_BEHALF_PROMPT_LABEL,
  approveOnBehalfTitle,
  approveOnBehalfMessage,
  approveOnBehalfConfirmLabel,
} from '../utils/approveOnBehalfWording';

/**
 * The 2026-08-18 owner-ruled confirmation before a manager approves a step
 * whose named approver is someone else — the incident: Jake, a supervisor,
 * pressed Approve on Department Orders and it worked (he holds
 * `canManageDepartmentOrders`, which lets a manager act on ANY step in the
 * department, not only their own — 🔀 2026-09-11: that is still TRUE OF THE
 * SERVER and untouched, but the LIST no longer advertises it. `GET /api/orders/`
 * `department` used to force `can_action` true on every submitted order for
 * such a caller, so a plain `Approve` appeared on other people's steps saying
 * nothing about it; that override is deleted (owner ruling A, after Jake hit
 * the same confusion a second time), and the on-behalf path is now reached
 * through an explicitly labelled *"Approve on behalf of …"* menu item. THIS
 * DIALOG IS UNCHANGED and still stands in front of it), then approved the SAME order again two
 * minutes later from a different page, this time genuinely acting on a
 * colleague's step. Nothing was broken — the app simply never told him he
 * was doing something out of the ordinary. Owner's ruling: *"if a supervisor
 * or buyer overides/forces through an approval that is not on their name
 * … lets have a popup explaing and asking if they want to overide … then
 * it explains and teaches the user."*
 *
 * 🔑 GATE, NOT DECORATION. Callers must not issue the approve request until
 * `onConfirm` fires — see `pages/DepartmentOrders.js`'s `requestApprove` and
 * `components/ApprovalStatus.js`'s `handleApprove` for the two shapes this
 * takes (a deferred `runRowAction`/`handleAction` call held in page state,
 * and an awaited Promise respectively). The SERVER decides authority either
 * way; this is a UI convenience that teaches, per the ruling above, and
 * never a second enforcement point.
 *
 * 🔑 WHO IS SHOWN THE DIALOG, decided by `utils/orderStepInfo.js`'s
 * `isApprovingSomeoneElsesStep` / `isDifferentApprover` — compared on
 * `approver_user_id`, NEVER on `approver_name` (display names are not
 * unique in this app), and a NULL `approver_user_id` ("an ambiguous
 * template name binds NOBODY") never fires this dialog: there is no real
 * person to name, so there is nothing true to say. See that file's
 * docblock for the full reasoning, which is shared with `canPokeOrder`.
 *
 * 🔑 `stepCount` is OPTIONAL and, when supplied and > 1, makes the confirm
 * button AGREE with `utils/approvalStepCollapse.js#approveButtonLabel` —
 * the Approve button beside it may already read "Approve all 3 of Jake
 * Dimmer's steps" (the 2026-08-18 sequential same-approver collapse), and
 * this dialog must not contradict it with a different phrase for the same
 * fact. Omit it (as `DepartmentOrders.js` always must — its list endpoint
 * computes no collapse count) and the copy reads as one step.
 *
 * Built on the shared `ConfirmDialog` (focus trap, Escape, overlay close,
 * focus return) rather than hand-rolled — see that component's own
 * docblock: "a fifth hand-rolled modal in this repo would get some subset
 * of them subtly wrong." `HireMoneyWarningDialog.js` is the precedent for
 * this shape: a thin wrapper whose wording lives in one `utils/*.js` module.
 *
 * 🔀 **A REASON IS REQUIRED SINCE 2026-09-14** (owner: *"add the text input for
 * 'Next step → Approve on behalf of Test Buyer…' as well as this is still an
 * override"*). Built on `RequiredReasonDialog`, the core it shares with
 * `SkipApprovalsDialog`: Confirm stays disabled until the trimmed reason is
 * non-empty, the textarea is capped at `OVERRIDE_REASON_MAX`, and
 * `onConfirm(reason)` receives the trimmed string for the approve POST. The
 * PARENT owns `reason` / `onReasonChange` (controlled textarea — see that
 * core's docblock). The server refuses `400 approve_on_behalf_reason_required`
 * regardless; this only makes the refusal unnecessary.
 */
export default function ApproveOnBehalfDialog({
  open,
  approverName,
  stepCount,
  reason,
  onReasonChange,
  busy,
  onConfirm,
  onCancel,
}) {
  return (
    <RequiredReasonDialog
      open={open}
      title={approveOnBehalfTitle(approverName)}
      message={approveOnBehalfMessage(approverName, stepCount)}
      reasonLabel={APPROVE_ON_BEHALF_PROMPT_LABEL}
      reason={reason}
      onReasonChange={onReasonChange}
      reasonMaxLength={OVERRIDE_REASON_MAX}
      confirmLabel={approveOnBehalfConfirmLabel(approverName, stepCount)}
      cancelLabel="Cancel"
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
